import { useEffect, useState, useCallback } from 'react'
import { authApi, userApi, TelegramAuthResponse, AuthResponse } from '../api/endpoints'
import { User, TelegramUser } from '../types'
import { useTelegramBotAPI } from './useTelegramBotAPI'

interface AuthState {
  user: User | null
  telegramUser: TelegramUser | null
  initData: string
  isAuthenticated: boolean
  isLoading: boolean
  referralUrl: string
}

interface RegisterResult {
  success: boolean
  error?: string
}

interface CheckResult {
  isNewUser: boolean
}

const TOKEN_KEY = 'access_token'
const REFRESH_TOKEN_KEY = 'refresh_token'
const USER_ID_KEY = 'user_id'

function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

function clearAuthStorage(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(USER_ID_KEY)
}

function setAuthStorage(token: string, refreshToken: string | undefined, userId: number): void {
  localStorage.setItem(TOKEN_KEY, token)
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
  localStorage.setItem(USER_ID_KEY, String(userId))
}

export function useAuth() {
  const { user: tgUser, initData, ready } = useTelegramBotAPI()
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    telegramUser: null,
    initData: '',
    isAuthenticated: getAccessToken() !== null,
    isLoading: true,
    referralUrl: '',
  })

  useEffect(() => {
    if (tgUser) {
      setAuthState(prev => ({ ...prev, telegramUser: tgUser }))
    }
    if (initData) {
      setAuthState(prev => ({ ...prev, initData }))
    }
  }, [tgUser, initData])

  const checkAndLoadUser = useCallback(async (): Promise<CheckResult | null> => {
    if (!ready) return null

    if (!tgUser || !initData) {
      const token = getAccessToken()
      if (token) {
        try {
          const userData = await userApi.getProfile()
          setAuthState(prev => ({
            ...prev,
            user: userData,
            isAuthenticated: true,
            isLoading: false,
          }))
          return { isNewUser: false }
        } catch {
          clearAuthStorage()
          setAuthState(prev => ({ ...prev, isAuthenticated: false, isLoading: false }))
        }
      } else {
        setAuthState(prev => ({ ...prev, isLoading: false }))
      }
      return null
    }

    try {
      const result = await authApi.telegramAuth(initData)

      if ('user_info' in result && result.user_info) {
        const res = result as TelegramAuthResponse
        setAuthState(prev => ({
          ...prev,
          telegramUser: {
            id: res.user_info!.id,
            first_name: res.user_info!.first_name,
            last_name: res.user_info!.last_name,
            username: res.user_info!.username,
            photo_url: res.user_info!.photo_url,
            language_code: res.user_info!.language_code,
          },
          referralUrl: res.referral_url || prev.referralUrl,
          isLoading: false,
        }))
        return { isNewUser: true }
      }

      if ('token' in result && result.token) {
        const res = result as AuthResponse
        setAuthStorage(res.token, res.refresh_token, res.user_id)
        const userData = await userApi.getProfile()
        setAuthState(prev => ({
          ...prev,
          user: userData,
          isAuthenticated: true,
          isLoading: false,
        }))
        return { isNewUser: false }
      }
    } catch (error: any) {
      console.error('خطا در احراز هویت:', error)
      setAuthState(prev => ({
        ...prev,
        isAuthenticated: false,
        isLoading: false,
        referralUrl: error.response?.data?.referral_url || prev.referralUrl,
      }))
      if (error.response?.status === 400) {
        return { isNewUser: true } as CheckResult
      }
    }

    return null
  }, [tgUser, initData, ready])

  const register = useCallback(async (referralCode: string): Promise<RegisterResult> => {
    if (!tgUser || !initData) {
      return { success: false, error: 'لطفاً از طریق ربات تلگرام وارد شوید.' }
    }

    try {
      const res = await authApi.register({
        telegram_id: tgUser.id,
        username: tgUser.username || '',
        first_name: tgUser.first_name,
        last_name: tgUser.last_name || '',
        photo_url: tgUser.photo_url || '',
        referral_code: referralCode,
      }, initData)

      setAuthStorage(res.token, res.refresh_token, res.user_id)

      const userData = await userApi.getProfile()
      setAuthState(prev => ({
        ...prev,
        user: userData,
        isAuthenticated: true,
        isLoading: false,
      }))
      return { success: true }
    } catch (error: any) {
      const errMsg = error.response?.data?.message || 'خطا در ثبت‌نام. دوباره تلاش کنید.'
      const referralUrl = error.response?.data?.referral_url
      if (referralUrl && !authState.referralUrl) {
        setAuthState(prev => ({ ...prev, referralUrl }))
      }
      return { success: false, error: errMsg }
    }
  }, [tgUser, initData, authState.referralUrl])

  const logout = useCallback(() => {
    clearAuthStorage()
    setAuthState({
      user: null,
      telegramUser: authState.telegramUser,
      initData: authState.initData,
      isAuthenticated: false,
      isLoading: false,
      referralUrl: authState.referralUrl,
    })
  }, [authState.telegramUser, authState.initData, authState.referralUrl])

  return {
    ...authState,
    checkAndLoadUser,
    register,
    logout,
  }
}

export function useUser() {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchUser = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const userData = await userApi.getProfile()
      setUser(userData)
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در بارگذاری اطلاعات')
    } finally {
      setLoading(false)
    }
  }, [])

  const refreshUser = useCallback(async () => {
    await fetchUser()
  }, [fetchUser])

  useEffect(() => {
    fetchUser()
  }, [fetchUser])

  return { user, loading, error, refreshUser }
}
